import to from "await-to-js";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { Input } from "../../../../components/Input/Input";
import { Select } from "../../../../components/Select/Select";
import { Table } from "../../../../components/Table/Table";
import { ModalsContext } from "../../../../context/modals.context";
import { CentralRegistryContext } from "../../../../modules/ministry/centralRegistry/centralRegistry.context";
import { centralRegistryService } from "../../../../modules/ministry/centralRegistry/centralRegistry.service";
import { RegistarOMarkamaDuvanskihProizvodaPayload } from "../../../../modules/ministry/centralRegistry/centralRegistry.types";
import { RecordsContext } from "../../../../modules/obligee/records/records.context";
import { recordsService } from "../../../../modules/obligee/records/records.service";
import { mapSelectOptions } from "../../../../utils/map4select";
import style from "../CentralRegistry.module.scss";

const INITIAL_PARAMS = () => ({
  privredniSubjektPIB: null,
  sifraDuvanskogProizvoda: null,
  vrstaDuvanskogProizvoda: null,
  robnaMarkaId: null,
  pakovanje: null,
  tipObveznika: null,
});

export const BrandRegistry = () => {
  const { centralRegistry, setCentralRegistry } = useContext(
    CentralRegistryContext
  );
  const { records, setRecords } = useContext(RecordsContext);
  const { setError } = useContext(ModalsContext);
  const [params, setParams] = useState(INITIAL_PARAMS());

  const postRegistarOMarkamaDuvanskihProizvoda = useDebouncedCallback(
    async () => {
      const [err, res] = await to(
        centralRegistryService.postRegistarOMarkamaDuvanskihProizvoda(params)
      );
      if (err || !res.success) return setError(res?.errorMessage);
      setCentralRegistry({
        brandRegistryTable: res.data.registarOMarkamaDuvanskihProizvoda,
        filtersRegistryVrstaDuvanskih: res.data.filtersRegistryVrstaDuvanskih,
        filtersRegistryPakovanje: res.data.filtersRegistryPakovanje,
        filtersRegistryTipObveznika: res.data.filtersRegistryTipObveznika,
      });
    },
    400
  );

  const createRobneMarkePretraga = async (pretraga: string | null) => {
    const [err, res] = await to(
      recordsService.getAllTrademarks({ naziv: pretraga })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setRecords({ robneMarkePretraga: res.data });
  };

  const updateParams = (
    accessor: keyof RegistarOMarkamaDuvanskihProizvodaPayload,
    value: string | null
  ) => {
    setParams({ ...params, [accessor]: value });
  };

  useEffect(() => {
    postRegistarOMarkamaDuvanskihProizvoda();
    createRobneMarkePretraga(null);
  }, [params]);

  const colDef = [
    {
      title: t("QR_CODE.VRSTA_DUVANSKOG_PROIZVODA"),
      width: "105px",
      dataIndex: "vrstaDuvanskogProizvoda",
    },
    {
      title: t("TABLES.NAZIV_MARKE_DP"),
      width: "120px",
      dataIndex: "nazivMarkeDuvanskogProizvoda",
      render: (nazivMarkeDuvanskogProizvoda: string) => (
        <span style={{ textTransform: "none" }}>
          {nazivMarkeDuvanskogProizvoda}
        </span>
      ),
    },
    {
      title: t("TABLES.SIFRA_DP"),
      width: "60px",
      dataIndex: "sifraDuvanskogProizvoda",
    },
    {
      title: t("TABLES.REDNI_BR_IZ_REGISTRA"),
      width: "105px",
      dataIndex: "redniBrojIzRegistraOMarkama",
    },
    {
      title: t("TABLES.JEDINICA_MERE"),
      width: "130px",
      dataIndex: "jedinicaMere",
    },
    {
      title: t("TABLES.PODACI_MARKA"),
      children: [
        {
          title: t("COMMON.NAZIV"),
          width: "100px",
          dataIndex: "privredniSubjektUpisNaziv",
        },
        {
          title: t("COMMON.PIB"),
          width: "90px",
          dataIndex: "privredniSubjektUpisPIB",
        },
      ],
    },
    {
      title: t("TABLES.PODACI_IZVOD"),
      children: [
        {
          title: t("COMMON.NAZIV"),
          width: "100px",
          dataIndex: "privredniSubjektNaziv",
        },
        {
          title: t("COMMON.PIB"),
          width: "90px",
          dataIndex: "privredniSubjektPIB",
        },
      ],
    },
    {
      title: t("PLACEHOLDERS.TIP_OBVEZNIKA"),
      dataIndex: "tipObveznika",
      width: "95px",
    },
    {
      title: t("TABLES.PAKOVANJE"),
      dataIndex: "pakovanje",
      width: "95px",
    },
  ];

  return (
    <div className={style.mainContainer}>
      <div id="brand_registry">
        <h2>{t("NAVIGATION.REGISTAR_O_MARKAMA")}</h2>
        <div
          className={style.buttonContainer}
          style={{ display: "flex", gap: " 20px" }}
        >
          <Input
            name="pib"
            allowClear
            className="w-15"
            placeholder={t("PLACEHOLDERS.PIB")}
            onChange={({ target }) =>
              updateParams("privredniSubjektPIB", target.value)
            }
          />
          <Input
            name="sifra"
            allowClear
            className="w-15"
            onChange={({ target }) =>
              updateParams("sifraDuvanskogProizvoda", target.value)
            }
            placeholder={t("TABLES.SIFRA_DP")}
          />
          <Select
            allowClear
            className="w-15"
            onChange={(value) => updateParams("vrstaDuvanskogProizvoda", value)}
            placeholder={t("QR_CODE.VRSTA_DUVANSKOG_PROIZVODA")}
            value={params.vrstaDuvanskogProizvoda}
            options={mapSelectOptions({
              sourceArray: centralRegistry.filtersRegistryVrstaDuvanskih,
              valueAccessor: "id",
              labelAccessor: "name",
            })}
            name="vrsta-duvanskog-proizvoda"
          />
          <Select
            style={{ width: "50%" }}
            options={
              mapSelectOptions({
                sourceArray: records.robneMarkePretraga,
                valueAccessor: "id",
                labelAccessor: "naziv",
              }) || []
            }
            allowClear
            onChange={(value) => updateParams("robnaMarkaId", value)}
            placeholder={t("PLACEHOLDERS.IZABERI_BREND")}
            filterOption={(input, option) => {
              const label: any = option?.label;
              if (label) {
                return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }
              return [] as any;
            }}
            showSearch
            name="brend"
          />
          <Select
            allowClear
            className="w-15"
            onChange={(value) => updateParams("pakovanje", value)}
            placeholder={t("TABLES.PAKOVANJE")}
            value={params.pakovanje}
            options={mapSelectOptions({
              sourceArray: centralRegistry.filtersRegistryPakovanje,
              valueAccessor: "id",
              labelAccessor: "name",
            })}
            name="pakovanje"
          />
          <Select
            allowClear
            className="w-15"
            onChange={(value) => updateParams("tipObveznika", value)}
            placeholder={t("PLACEHOLDERS.TIP_OBVEZNIKA")}
            value={params.tipObveznika}
            options={mapSelectOptions({
              sourceArray: centralRegistry.filtersRegistryTipObveznika,
              valueAccessor: "id",
              labelAccessor: "name",
            })}
            name="tip-obveznika"
          />
        </div>
      </div>
      <Table
        columns={colDef}
        scrollConfig={["#brand_registry"]}
        dataSource={centralRegistry.brandRegistryTable}
        name="brand-registry-table"
      />
    </div>
  );
};
